/** @jsx isolateComponent */
import React from 'react';

import { keyframes, Interpolation } from '@emotion/core';
import { useDimensions } from '@zapier/design-system/src/hooks';

import {
  Animation,
  Colors,
  Shadows,
  Typography,
} from '@zapier/design-system/src/theme';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

export type TooltipPosition =
  | 'northwest'
  | 'north'
  | 'northeast'
  | 'east'
  | 'southeast'
  | 'south'
  | 'southwest'
  | 'west';

type TooltipProps = {
  /** Tooltip expands to accomodate content if true. */
  allowMultiline?: boolean;
  /**
   * Indicates whether the content is hidden from screenreaders.
   * This is useful when the `Tooltip` has already been associated
   * to a node via `aria-describedby` to prevent screenreaders from
   * reading the content of the `Tooltip` twice. <strong>Note that
   * some screenreaders like VoiceOver only read content associated
   * via `aria-describedby` when the element with that attribute is
   * interactive, like a `button` element.</strong>
   */
  ariaHidden?: boolean;
  /** The content to render inside of the Tooltip. */
  children: React.ReactNode;
  /** The position to place the tooltip relative to the parent element */
  position?: TooltipPosition;
  /** Unique id, intended for use with `aria-describedby` which is why it's required */
  id: string;
  /** Indicates whether `Tooltip` can be hovered, clicked, etc. */
  isInteractive?: boolean;
};

const distance = '5px';

const tooltipOpenAnimation = keyframes({
  '0%': {
    opacity: 0,
  },
  '100%': {
    opacity: 1,
  },
});

const positionStyles = {
  northwest: {
    bottom: `calc(100% + ${distance})`,
    left: 0,
  },

  north: {
    bottom: `calc(100% + ${distance})`,
    left: '50%',
    transform: 'translateX(-50%)',
  },

  northeast: {
    bottom: `calc(100% + ${distance})`,
    right: 0,
  },

  east: {
    left: `calc(100% + ${distance})`,
    top: '50%',
    transform: 'translateY(-50%)',
  },

  southeast: {
    top: `calc(100% + ${distance})`,
    right: 0,
  },

  south: {
    top: `calc(100% + ${distance})`,
    left: '50%',
    transform: 'translateX(-50%)',
  },

  southwest: {
    top: `calc(100% + ${distance})`,
    left: 0,
  },

  west: {
    right: `calc(100% + ${distance})`,
    top: '50%',
    transform: 'translateY(-50%)',
  },
};

const singleLineHeight = 20;
const defaultPosition = 'south';

const Styles = labelStyles(__filename, {
  root: (
    { position = defaultPosition, allowMultiline, isInteractive }: TooltipProps,
    isMultiline: boolean
  ) => [
    {
      color: Colors.white,
      textAlign: 'center',
      borderRadius: 100,
      position: 'absolute',
      padding: '0 1em',
      zIndex: 2,
      boxShadow: Shadows.elevation5,
      backgroundColor: Colors.grey900,
      maxWidth: 300,
      animation: `${Animation.transitionDuration} ${Animation.transitionTimingFunction} ${tooltipOpenAnimation}`,
      ...Typography.altText900,
      fontWeight: 600,

      // Increase mouse area so tooltip doesn't disappear when
      // mousing over a gap between its toggle and the tooltip itself.
      '&::before': {
        content: '""',
        position: 'absolute',
        top: `-${distance}`,
        right: `-${distance}`,
        bottom: `-${distance}`,
        left: `-${distance}`,
        zIndex: -1,
      },
    },

    withModifier(position, positionStyles[position]),

    isMultiline &&
      withModifier('isMultiline', {
        borderRadius: 10,
        padding: '10px 15px 15px 15px',
        textAlign: 'left',
      }),

    !allowMultiline &&
      withModifier('singleLine', {
        height: singleLineHeight,
      }),

    !isInteractive &&
      withModifier('nonInteractive', {
        pointerEvents: 'none',
      }),
  ],

  content: (props: TooltipProps) => [
    {
      display: 'block',
    },

    // Separate node to truncate content. It's important
    // to apply `overflow: hidden` here and not on `root`
    // otherwise the `::before` will be clipped.
    !props.allowMultiline &&
      withModifier('singleLine', {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }),
  ],
});

/**
 * Tooltip component, a floating tooltip to display additional information.
 * Can be used on its own, or rendered by the TooltipWrapper.
 *
 * Tags: core-ui
 */
const Tooltip: React.FC<TooltipProps> = props => {
  /**
   * Get the height of the tooltip to determine if it has multiple lines. The tooltip's content could
   * only be one line but have the `allowMultiline` prop set to true, so we need the height to know
   * for sure if the tooltip has multiple lines.
   */
  const [ref, { height }] = useDimensions();
  const isMultiline = height > singleLineHeight;

  return (
    <span
      aria-hidden={props.ariaHidden}
      css={Styles.root(props, isMultiline) as Interpolation}
      data-testid={`tooltip-${props.position}`}
      id={props.id}
      ref={ref}
      role="tooltip"
    >
      <span css={Styles.content(props) as Interpolation}>{props.children}</span>
    </span>
  );
};

Tooltip.defaultProps = {
  allowMultiline: false,
  position: defaultPosition,
};

export default Tooltip;
